import React, { useState, useEffect } from 'react';
import {
  Container, Header, Box, Button, Alert, SpaceBetween, Spinner, Badge, Checkbox
} from '@cloudscape-design/components';

/**
 * Scene plan card for the lifestyle generation flow (v2 — Flux pipeline).
 *
 * Key change from v1: options are selected via CHECKBOXES (multi-select)
 * instead of radio buttons (single-select). The user checks which options
 * they want generated — each checked option = 1 Flux call = 1 output image.
 *
 * Props:
 *   plan: { product_description, reference_style, options[], flags[] }
 *   loading: boolean
 *   error: string | null
 *   referenceThumbUrl: string | null
 *   originalThumbUrl: string | null
 *   readOnly: boolean
 *   selectedIndices: number[]                      — which options are checked
 *   onSelectionChange: (indices: number[]) => void — called when checkboxes change
 *   onApprove: () => void
 *   onAdjust: () => void
 *   inputs: object                                 — user's form inputs for display
 *   useCase: string                                — 'pdp' | 'ad' | 'social'
 */
export default function ScenePlanCard({
  plan, loading, error, referenceThumbUrl, readOnly,
  selectedIndices, onSelectionChange, onApprove, onAdjust,
  originalThumbUrl, inputs, useCase,
}) {
  // Default: first option checked when plan arrives
  useEffect(() => {
    if (plan && Array.isArray(plan.options) && plan.options.length > 0) {
      if (!selectedIndices || selectedIndices.length === 0) {
        if (onSelectionChange) onSelectionChange([0]);
      }
    }
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOption = (index) => {
    if (readOnly) return;
    const current = selectedIndices || [];
    if (current.includes(index)) {
      // Uncheck — but don't allow unchecking all (at least 1 must be selected)
      const next = current.filter(i => i !== index);
      if (next.length > 0 && onSelectionChange) onSelectionChange(next);
    } else {
      // Check
      if (onSelectionChange) onSelectionChange([...current, index].sort());
    }
  };

  if (loading) {
    return (
      <Container header={<Header variant="h3">🔍 Analyzing your image…</Header>}>
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <Spinner />
          <Box color="text-body-secondary">
            Claude is reviewing the product and drafting three scene options. This usually takes 8–15 seconds.
          </Box>
        </SpaceBetween>
      </Container>
    );
  }

  if (error) {
    return (
      <Container header={<Header variant="h3">Preview error</Header>}>
        <SpaceBetween size="s">
          <Alert type="error">{error}</Alert>
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={onApprove}>Generate anyway</Button>
            <Button onClick={onAdjust}>Adjust form</Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    );
  }

  if (!plan) return null;

  const options = Array.isArray(plan.options) ? plan.options : [];
  const hasReference = !!plan.reference_style;
  const flagAlertType = (sev) => sev === 'error' ? 'error' : sev === 'warn' ? 'warning' : 'info';
  const checkedCount = (selectedIndices || []).length;

  const headerDesc = readOnly
    ? `Generated ${checkedCount} image${checkedCount !== 1 ? 's' : ''} from selected option${checkedCount !== 1 ? 's' : ''}.`
    : `Check the options you want to generate (${checkedCount} selected = ${checkedCount} image${checkedCount !== 1 ? 's' : ''}).`;

  return (
    <Container header={<Header variant="h3" description={headerDesc}>🧭 Generation plan</Header>}>
      <SpaceBetween size="m">
        {plan.preview_unavailable && (
          <Alert type="warning">
            Preview is temporarily unavailable — you can proceed with a custom prompt.
          </Alert>
        )}

        {plan.product_description && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>What I see</Box>
            <SpaceBetween direction="horizontal" size="s" alignItems="start">
              <ClickableThumb src={originalThumbUrl} alt="Original product" />
              <Box>{plan.product_description}</Box>
            </SpaceBetween>
          </div>
        )}

        {hasReference && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>Reference style</Box>
            <SpaceBetween direction="horizontal" size="s" alignItems="start">
              <ClickableThumb src={referenceThumbUrl} alt="Reference image" />
              <Box>{plan.reference_style}</Box>
            </SpaceBetween>
          </div>
        )}

        {options.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>
              {readOnly
                ? `Selected option${checkedCount !== 1 ? 's' : ''}`
                : `Select options to generate (${options.length} proposed)`}
            </Box>
            <SpaceBetween size="xs">
              {options.map((opt, i) => {
                const isChecked = (selectedIndices || []).includes(i);
                // In readOnly mode, show only checked options
                if (readOnly && !isChecked) return null;
                return (
                  <OptionTile
                    key={i}
                    option={opt}
                    index={i}
                    checked={isChecked}
                    readOnly={readOnly}
                    onToggle={() => toggleOption(i)}
                    useCase={useCase}
                  />
                );
              })}
            </SpaceBetween>
          </div>
        )}

        {plan.flags && plan.flags.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>Heads up</Box>
            <SpaceBetween size="xxs">
              {plan.flags.map((f, i) => (
                <Alert key={i} type={flagAlertType(f.severity)}>{f.message}</Alert>
              ))}
            </SpaceBetween>
          </div>
        )}

        <InputsPanel inputs={inputs} />

        {!readOnly && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              onClick={onApprove}
              disabled={options.length === 0 && !plan.preview_unavailable}
            >
              🚀 Generate {checkedCount} image{checkedCount !== 1 ? 's' : ''}
            </Button>
            <Button onClick={onAdjust}>Adjust</Button>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
}

/** A single option tile with a checkbox. */
function OptionTile({ option, index, checked, readOnly, onToggle, useCase }) {
  const framingLabel = option.camera_framing
    ? option.camera_framing.replace('-', ' ')
    : 'medium';
  const containerStyle = {
    padding: 12,
    borderRadius: 8,
    border: checked ? '2px solid #0972d3' : '1px solid #e9ebed',
    background: checked ? '#f2f8fd' : '#ffffff',
    cursor: readOnly ? 'default' : 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  };

  // Show ad_copy info for Ad use-case
  const adCopy = option.ad_copy;
  const socialText = option.social_text;

  return (
    <div
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      onClick={readOnly ? undefined : onToggle}
      onKeyDown={readOnly ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
      }}
      style={containerStyle}
    >
      <SpaceBetween size="xs">
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {!readOnly && (
            <Checkbox checked={checked} onChange={onToggle} />
          )}
          <Box variant="strong">{option.title || `Option ${index + 1}`}</Box>
          <Badge>{framingLabel}</Badge>
        </SpaceBetween>

        {option.rationale && (
          <Box fontSize="body-s" color="text-body-secondary">
            {option.rationale}
          </Box>
        )}

        {Array.isArray(option.scene_plan) && option.scene_plan.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {option.scene_plan.map((b, j) => (
              <li key={j} style={{ marginBottom: 2, fontSize: 13 }}>{b}</li>
            ))}
          </ul>
        )}

        {/* Show text that will be rendered in the image (Ad use-case) */}
        {useCase === 'ad' && adCopy && adCopy.headline && (
          <div style={{ marginTop: 4, padding: '6px 10px', background: '#f0f4f8', borderRadius: 4, fontSize: 12 }}>
            <Box fontSize="body-s" color="text-body-secondary">Text in image:</Box>
            <Box fontSize="body-s"><strong>{adCopy.headline}</strong></Box>
            {adCopy.subtext && <Box fontSize="body-s">{adCopy.subtext}</Box>}
            {adCopy.cta_label && <Box fontSize="body-s">CTA: {adCopy.cta_label}</Box>}
          </div>
        )}

        {/* Social text indicator */}
        {useCase === 'social' && socialText && socialText.has_text && (
          <div style={{ marginTop: 4, padding: '6px 10px', background: '#f0f4f8', borderRadius: 4, fontSize: 12 }}>
            <Box fontSize="body-s" color="text-body-secondary">Text in image: {socialText.text_content}</Box>
          </div>
        )}
      </SpaceBetween>
    </div>
  );
}

function ClickableThumb({ src, alt, size = 96 }) {
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', lineHeight: 0 }}>
      <img
        src={src} alt={alt}
        style={{ width: size, height: size, objectFit: 'cover', borderRadius: 6, border: '1px solid #e9ebed', cursor: 'zoom-in' }}
      />
    </a>
  );
}

function InputsPanel({ inputs }) {
  if (!inputs) return null;
  const ordered = [
    ['Use case', formatUseCase(inputs.useCase)],
    ['Aspect ratio', inputs.aspectRatio],
    ['Scene description', inputs.customPrompt],
    ['Include person', inputs.includePerson === 'yes' ? 'Yes' : ''],
    ['Model appearance', inputs.modelAppearance && inputs.modelAppearance !== 'default' ? formatModelAppearance(inputs.modelAppearance) : ''],
    ['Headline', inputs.adCopy && inputs.adCopy.headline],
    ['Subtext', inputs.adCopy && inputs.adCopy.subtext],
    ['CTA', inputs.adCopy && inputs.adCopy.cta_label],
    ['Tweak applied', inputs.tweak && formatTweak(inputs.tweak)],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');

  if (ordered.length === 0) return null;

  return (
    <div>
      <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>Your inputs</Box>
      <div style={{
        display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px',
        padding: '10px 12px', borderRadius: 8, background: '#fafbfc', border: '1px solid #eaeded',
      }}>
        {ordered.map(([label, value]) => (
          <React.Fragment key={label}>
            <Box fontSize="body-s" color="text-body-secondary">{label}</Box>
            <Box fontSize="body-s">{String(value)}</Box>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function formatUseCase(v) {
  if (!v) return '';
  const map = { pdp: 'Product page (PDP)', ad: 'Ad creative', social: 'Social media' };
  return map[v] || v;
}

function formatTweak(v) {
  if (!v) return '';
  const map = {
    brighter: 'Brighter', warmer: 'Warmer', premium: 'More premium',
    minimal: 'More minimal', 'different-angle': 'Different angle', 'zoom-in': 'Zoom in',
    'indian-model': 'Indian model', 'different-model': 'Different model',
  };
  return map[v] || v;
}

function formatModelAppearance(v) {
  if (!v) return '';
  const map = {
    'indian-woman': 'Indian woman',
    'indian-man': 'Indian man',
    'indian-child-boy': 'Indian boy (child)',
    'indian-child-girl': 'Indian girl (child)',
    'indian-teen-boy': 'Indian teen boy',
    'indian-teen-girl': 'Indian teen girl',
    'east-asian-woman': 'East Asian woman',
    'east-asian-man': 'East Asian man',
    'white-woman': 'White woman',
    'white-man': 'White man',
    'white-child': 'White child',
    'black-woman': 'Black woman',
    'black-man': 'Black man',
  };
  return map[v] || v;
}
